import { Link } from "@tanstack/react-router";
import { ArrowUpRight, ChevronRight, LifeBuoy } from "lucide-react";

import { HelpSearch } from "./components/help-search";
import { HelpCategoryIcon } from "@/features/help/components/help-category-icon";
import { StillStuck } from "@/features/help/components/still-stuck";
import type { HelpCentre } from "@/features/help/types";
import { displayTitle, Eyebrow, GoLink, Section, SectionHead } from "@/features/portal/components/portal-ui";
import { cn } from "@/lib/utils";
import { m } from "@/paraglide/messages.js";

/** How many articles lead the page under "Popular". */
const POPULAR_LIMIT = 6;

/** The words that open the help centre, inside the head card, with the search under them. */
export const HelpHero = ({ centre }: { centre: HelpCentre | null }) => (
  <div className="relative mx-auto max-w-[880px] px-1 pt-14 pb-4 text-center md:pt-20 md:pb-8">
    <Eyebrow>{m.help_title()}</Eyebrow>
    <h1 className={cn(displayTitle, "mt-6")}>{m.help_hero_title()}</h1>
    <p className="mx-auto mt-6 max-w-[52ch] text-lg leading-[1.55] text-pretty text-muted-foreground">
      {m.help_hero_lede()}
    </p>
    {centre && centre.categories.length > 0 ? <HelpSearch centre={centre} /> : null}
  </div>
);

/** Before the first article goes live: what the help centre will be, and a person to ask meanwhile. */
const EmptyHelp = () => (
  <Section>
    <div className="relative isolate overflow-hidden rounded-[32px] border border-border bg-background px-6 py-14 text-center md:py-20">
      <div aria-hidden className="pointer-events-none absolute inset-x-8 -top-6 -z-10 grid gap-3 opacity-60 blur-[1px] sm:grid-cols-3">
        {[0, 1, 2].map((index) => (
          <div key={index} className={cn("h-32 rounded-[24px] bg-linear-to-b from-[var(--site-wash)] to-transparent", index === 1 && "translate-y-5")} />
        ))}
      </div>
      <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-[var(--site-wash)] text-[var(--site-ink)]">
        <LifeBuoy aria-hidden className="size-5" />
      </span>
      <h2 className="mx-auto mt-6 max-w-[22ch] text-[clamp(1.6rem,3vw,2.25rem)] leading-[1.08] font-medium tracking-[-0.04em] text-balance">
        {m.help_empty_title()}
      </h2>
      <p className="mx-auto mt-4 max-w-[46ch] text-[15px] leading-relaxed text-muted-foreground">{m.help_empty_body()}</p>
      <div className="mt-8 flex justify-center">
        <GoLink to="/contact">{m.help_stuck_cta()}</GoLink>
      </div>
    </div>
  </Section>
);

/**
 * Everything under the search: the articles people open most, then every topic with its
 * articles, and the way to a person. Each topic is an anchor (`/help#bookings`), which is where an
 * article's breadcrumb leads back to.
 */
export const HelpCentreView = ({ centre }: { centre: HelpCentre }) => {
  if (centre.categories.length === 0) return <EmptyHelp />;

  const popular = centre.categories.flatMap((category) => category.articles.filter((article) => article.popular)).slice(0, POPULAR_LIMIT);

  return (
    <>
      {popular.length ? (
        <Section className="md:pt-16">
          <SectionHead title={m.help_popular_title()} />
          <ul className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {popular.map((article) => (
              <li key={article.slug}>
                <Link
                  to="/help/$slug"
                  params={{ slug: article.slug }}
                  className="group flex h-full flex-col justify-between gap-6 rounded-[28px] border border-border bg-background p-6 transition-colors hover:bg-[var(--site-tint)]"
                >
                  <span>
                    <span className="block text-lg leading-snug font-medium tracking-[-0.02em]">{article.title}</span>
                    {article.summary ? (
                      <span className="mt-2 line-clamp-2 block text-[15px] leading-normal text-muted-foreground">{article.summary}</span>
                    ) : null}
                  </span>
                  <span className="grid size-9 place-items-center self-end rounded-full bg-muted transition-transform duration-200 group-hover:rotate-45">
                    <ArrowUpRight aria-hidden className="size-4" />
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </Section>
      ) : null}

      <Section className={popular.length ? undefined : "md:pt-16"}>
        <SectionHead title={m.help_browse_title()} aside={m.help_browse_aside()} />
        <div className="grid gap-3 md:grid-cols-2">
          {centre.categories.map((category) => (
            <section
              key={category.slug}
              id={category.slug}
              aria-labelledby={`${category.slug}-title`}
              className="scroll-mt-6 rounded-[28px] border border-border bg-background p-6 md:p-7"
            >
              <div className="flex items-start gap-4">
                <HelpCategoryIcon icon={category.icon} />
                <div className="min-w-0">
                  <h3 id={`${category.slug}-title`} className="text-xl font-medium tracking-[-0.03em]">
                    {category.name}
                  </h3>
                  {category.description ? (
                    <p className="mt-1 text-[15px] leading-normal text-muted-foreground">{category.description}</p>
                  ) : null}
                </div>
              </div>
              <ul className="mt-5 -mb-2 divide-y divide-border">
                {category.articles.map((article) => (
                  <li key={article.slug}>
                    <Link
                      to="/help/$slug"
                      params={{ slug: article.slug }}
                      className="group flex items-center justify-between gap-4 py-3 text-[15px] font-medium"
                    >
                      <span className="transition-colors group-hover:text-[var(--site-ink)]">{article.title}</span>
                      <ChevronRight aria-hidden className="size-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}
        </div>
      </Section>

      <StillStuck />
    </>
  );
};
